/* eslint-disable max-lines-per-function, max-lines */
import get from "lodash.get";
import { EventBus, Events } from "jiffy-store";
import { track } from "jiffy-analytics";

import updateRestockText from "components/jiffy/product-details-quantity-form/update-restock-text";
import showProductModule from "components/jiffy/product-details-quantity-form/show-product";
import { productEngagementTracker } from "components/jiffy/product-details/product-engagement-tracker";

import { requestLocalStorage } from "../../../common/utils/browser";
import {
  updateUrlForColorChange,
  shouldUseEnhancedUrls,
  generateEnhancedUrl,
  parameterizeColor
} from "../../../common/utils/enhanced-url-generator";
import {
  isColorUrlPersistEnabled,
  isEnhancedColorUrlPersistEnabled,
  isMadeDedicatedPdp,
  isCanonicalPdpColorLink,
  hasMadeContextParam,
  setColorHash,
  getColorFromHash
} from "../../../common/utils/color-url-persist";
import "./product-colors-slider.scss";

const SELECTORS = {
  colorPicker: "js-product-color-picker",
  item: "js-color-variant-selector",
  get itemActive() {
    if (document.querySelector(".js-product-color-palette")) {
      return "product-color-palette__color-item--active";
    }
    return "product-colors-slider__item-link--active";
  },
  itemActiveJS: "js-color-variant-selector-active",
  modalContent: "#color-variant-selector-content",
  scrollToActive: "js-scroll-to-active-color",
  slider: "js-product-colors-slider",
  sliderArrow: "js-colors-slider-arrow",
  sliderControls: "js-colors-slider-controls",
  itemSameDayBadge: ".js-product-colors-slider-same-day",
  itemSameDayBadgeText: ".js-product-colors-slider-same-day-text"
};

const $getSameDayBadges = () => document.querySelectorAll(SELECTORS.itemSameDayBadge);
const hideSameDayBadges = () => $getSameDayBadges().forEach($el => $el.classList.add("hidden"));

// Helper function to get URL parameters
const getUrlParams = () => {
  const { searchParams } = new URL(window.location);
  const params = new URLSearchParams();
  ["page", "made", "size"].forEach(name => {
    const param = searchParams.get(name);
    if (param !== null) {
      params.set(name, param);
    }
  });
  return params;
};

// Update canonical and social meta tags when color changes (for enhanced URLs)
const updateCanonicalUrlDirect = colorName => {
  // Only update when we are on an enhanced URL path
  if (!shouldUseEnhancedUrls()) return;

  // Build enhanced path using multi-color-aware generator and preserve query params
  const enhancedPath = generateEnhancedUrl(colorName, true);
  const newFullUrl = window.location.origin + enhancedPath;

  // Update canonical tag
  const canonical = document.querySelector("link[rel='canonical']");
  if (canonical) {
    canonical.setAttribute("href", newFullUrl);
  }

  // Update Open Graph URL
  const ogUrl = document.querySelector("meta[property='og:url']");
  if (ogUrl) {
    ogUrl.setAttribute("content", newFullUrl);
  }

  // Update Twitter URL
  const twitterUrl = document.querySelector("meta[name='twitter:url']");
  if (twitterUrl) {
    twitterUrl.setAttribute("content", newFullUrl);
  }
};

const deactivateItem = $context => {
  const $itemActive = $context.querySelector(`.${SELECTORS.itemActive}`);
  if ($itemActive) {
    $itemActive.classList.remove(SELECTORS.itemActive);
    $itemActive.classList.remove(SELECTORS.itemActiveJS);
  }
};

const scrollToActive = $colorsSlider => {
  const $colorPicker = $colorsSlider.querySelector(`.${SELECTORS.scrollToActive}`);
  if (!$colorPicker) return;

  const $activeItem = $colorPicker.querySelector(`.${SELECTORS.itemActive}`);
  if (!$activeItem) return;

  const shift = ($colorPicker.offsetWidth - $activeItem.offsetWidth) / 2;
  const targetLeft = $activeItem.offsetLeft - shift;

  $colorPicker.scroll({
    behavior: "auto",
    left: targetLeft
  });
};

const colorUpdateObserver = $context => {
  EventBus.on(Events.PRODUCT_COLOR_SELECTED, (evt, { color }) => {
    // Next event loop
    setTimeout(() => {
      // render new color name
      const $currentColor = document.querySelector(".js-current-color-name");
      if ($currentColor) {
        $currentColor.innerHTML = color;
      }
      // add active state to color trigger if color was changed from modal
      const $itemActive = $context.querySelector(`.${SELECTORS.itemActive}`);
      const currentColor = get($itemActive, "dataset.color");
      if (color !== currentColor) {
        const $nextActiveItem = $context.querySelector(`[data-color="${CSS.escape(color)}"]`);
        if ($nextActiveItem) {
          deactivateItem($context);
          $nextActiveItem.classList.add(SELECTORS.itemActive);
          $nextActiveItem.classList.add(SELECTORS.itemActiveJS);
          scrollToActive($context);
        }
      }
    });
  });
};

// Shared color activation used by both user clicks and silent #color
// pre-selection. When userInitiated is false (PIP-3358 deep-link pre-select),
// skip engagement tracking, the EventBus publish, the localStorage flag, and
// the URL rewrite — the user never clicked, so none of those side effects
// should fire.
const activateColorItem = ($context, $colorItem, { userInitiated }) => {
  const { color, compositionId, supportsMade, madeDedicatedPdp } = $colorItem.dataset;

  const $variantContent = document.querySelector(SELECTORS.modalContent);
  const previousColorGroup = $variantContent
    ? $($variantContent)
        .find("a.js-color-variant-selector.product-color-picker__item--active")
        .data("colorGroup")
    : null;

  if (userInitiated) {
    requestLocalStorage()?.setItem("PDPcolorPickerClicked", true);
  }
  updateRestockText(color);

  // remove active state
  deactivateItem($context);
  // add active state
  $colorItem.classList.add(SELECTORS.itemActive);
  $colorItem.classList.add(SELECTORS.itemActiveJS);

  if (userInitiated) {
    // Start engagement tracking with 10-second timer
    const productContainer = document.getElementById("product-page-container");
    const productId = productContainer?.dataset?.productId;
    if (productId && compositionId) {
      productEngagementTracker.trackCompositionChange({
        compositionId,
        compositionName: color,
        productId
      });
    }
  }

  showProductModule.productVariantColorModal.selectColor(
    $colorItem.dataset || {},
    previousColorGroup,
    { userInitiated }
  );

  if (userInitiated) {
    // publish event
    EventBus.publish(Events.PRODUCT_COLOR_SELECTED_FROM_SLIDER, this, { color });
  }

  if (!userInitiated) return;

  // Check if we should use enhanced URLs (either already on enhanced URL or is made dedicated PDP)
  const useEnhanced =
    shouldUseEnhancedUrls() || (madeDedicatedPdp === "true" && supportsMade === "true");

  if (useEnhanced) {
    if (isEnhancedColorUrlPersistEnabled() && !hasMadeContextParam($colorItem)) {
      // PIP-3360: keep the current URL path (base .html or an inbound color
      // path); carry the selected color in the #color hash only. The PIP-471
      // canonical/og:url/twitter:url client rewrite is suppressed — the server
      // already emits the style base .html as the canonical for this surface.
      // Made sample/edit/flash swatches (?sample=/?sameday=/?edit=) are excluded
      // so they keep their custom URL (else branch), mirroring the server's
      // crawlable_made_pdp_color_path made-context carve-out.
      setColorHash(color);
    } else {
      updateUrlForColorChange(color);

      // Update canonical URL for enhanced URLs (PIP-471 self-canonical requirement)
      updateCanonicalUrlDirect(color);
    }
  } else if (
    (isColorUrlPersistEnabled() || (isEnhancedColorUrlPersistEnabled() && isMadeDedicatedPdp())) &&
    isCanonicalPdpColorLink($colorItem)
  ) {
    // PIP-3358: keep the canonical URL; carry color in the #color hash. Gated
    // to anchors targeting THIS canonical PDP — legacy made sample/edit/flash
    // swatches carry made-context hrefs (sample/asgid/sameday) that must keep
    // their custom URL instead of collapsing to a hash.
    // PIP-3360: the enhanced flag extends the same behavior to the made base
    // `.html` page, where the palette/slider swatch anchors target the base
    // path. The palette anchor carries no per-swatch made dataset (only the
    // slider item does), so useEnhanced above is false on a made-base palette
    // click; without this PAGE-level made gate (isMadeDedicatedPdp, from
    // data-made-pdp) the blanks-flag-off case would fall to the legacy else
    // and drop the #color fragment. Gated on the page made signal so the
    // enhanced flag never leaks onto blanks /{brand}-{style}.html pages.
    setColorHash(color);
  } else {
    const params = getUrlParams();
    const path = `${$colorItem.pathname}${$colorItem.search}${
      params.size ? `&${params.toString()}` : ""
    }`;
    window.history.replaceState(null, null, path);
  }
};

const initChangeColorHandler = $context =>
  $context.addEventListener("click", e => {
    const { target } = e;
    const $colorItem = target.classList.contains(SELECTORS.item)
      ? target
      : target.closest(`.${SELECTORS.item}`);

    if ($colorItem) {
      e.preventDefault(); // Always prevent default navigation
      e.stopPropagation(); // Also prevent event bubbling

      activateColorItem($context, $colorItem, { userInitiated: true });
    }
  });

const handleArrowClick = ($colorPicker, $arrow) => {
  const { direction } = $arrow.dataset;
  const segmentWidth = Math.round($colorPicker.scrollWidth / $colorPicker.childElementCount);
  const currentItem = Math.round($colorPicker.scrollLeft / segmentWidth);
  let targetLeft;
  if (direction === "left") {
    targetLeft = (currentItem - 2) * segmentWidth;
  } else if (direction === "right") {
    targetLeft = (currentItem + 2) * segmentWidth;
  }

  targetLeft !== undefined &&
    $colorPicker.scroll({
      behavior: "smooth",
      left: targetLeft
    });
};

const handleSliderScroll = ($colorPicker, $arrows) => {
  const { offsetWidth, scrollLeft, scrollWidth } = $colorPicker;
  const [$arrowLeft, $arrowRight] = $arrows;
  const isArrowLeftDisabled = $arrowLeft.hasAttribute("disabled");
  const isArrowRightDisabled = $arrowRight.hasAttribute("disabled");

  // slider is at the leftmost position
  if (scrollLeft === 0) {
    !isArrowLeftDisabled && $arrowLeft.setAttribute("disabled", true);
  } else {
    isArrowLeftDisabled && $arrowLeft.removeAttribute("disabled");
  }

  // slider is at the rightmost position
  if (scrollWidth <= offsetWidth + scrollLeft) {
    !isArrowRightDisabled && $arrowRight.setAttribute("disabled", true);
  } else {
    isArrowRightDisabled && $arrowRight.removeAttribute("disabled");
  }
};

const willRenderSliderArrows = ($colorPicker, $colorsSlider) => {
  const { offsetWidth, scrollWidth } = $colorPicker;
  const $sliderControls = $colorsSlider.querySelectorAll(`.${SELECTORS.sliderControls}`);
  if ($sliderControls.length === 0) return;

  if (scrollWidth > offsetWidth) {
    Array.from($sliderControls).forEach($control => {
      $control.classList.remove("hidden");
    });
  }
};

const initSliderControls = $colorsSlider => {
  const $arrows = $colorsSlider.querySelectorAll(`.${SELECTORS.sliderArrow}`);
  if ($arrows.length === 0) return;

  const $colorPicker = $colorsSlider.querySelector(`.${SELECTORS.colorPicker}`);
  if (!$colorPicker) return;

  willRenderSliderArrows($colorPicker, $colorsSlider);

  Array.from($arrows).forEach($arrow => {
    $arrow.addEventListener("click", () => handleArrowClick($colorPicker, $arrow));
  });

  handleSliderScroll($colorPicker, $arrows);
  $colorPicker.addEventListener("scroll", () => handleSliderScroll($colorPicker, $arrows), {
    passive: true
  });
};

// Non-color hashes that legitimately appear on PDP URLs but are matched in JS
// rather than by an element id (see product-related-products.js). Hashes that
// target a real on-page element id are detected dynamically below, so this
// list only needs the pseudo-anchors.
const KNOWN_PDP_ANCHORS = ["comparables"];

// A hash that targets an in-page anchor (#comments, #reviews-summary,
// #similar-items, …) is navigation, not a failed color share — it must not
// pollute the unmatched-color telemetry.
const isInPageAnchor = hashSlug => {
  const raw = window.location.hash.replace(/^#/, "");
  return (
    KNOWN_PDP_ANCHORS.includes(hashSlug) ||
    Boolean(document.getElementById(raw) || document.getElementById(hashSlug))
  );
};

// PIP-3358: on load, when a #color hash is present and matches a color item that
// is not already active, pre-select it so deep links to /{brand}-{style}.html#color
// surface the shared color. Match on SLUGS both ways (parameterizeColor) so colors
// with punctuation/slashes/hyphens — "Black/ White", "Faded Blue*", hyphenated
// names — round-trip correctly; a de-slug-then-compare would miss those.
// Activate silently (userInitiated: false) — the user never clicked, so engagement
// tracking, the EventBus publish, and the localStorage flag must not fire.
//
// Deliberately NOT gated on isColorUrlPersistEnabled(): hash WRITING is
// flag-gated, but a shared/bookmarked #color link must keep resolving even when
// the recipient session has the flag off (mixed rollout states, rollback, the
// cache-TTL window after a toggle). With the flag off nothing writes hashes, so
// honoring an inbound one is side-effect-free.
const preselectColorFromHash = $context => {
  const hashColor = getColorFromHash();
  if (!hashColor) return;

  const hashSlug = parameterizeColor(hashColor);
  if (!hashSlug) return;

  const items = Array.from($context.querySelectorAll(`.${SELECTORS.item}`));
  const $match = items.find($item => parameterizeColor($item.dataset.color || "") === hashSlug);
  if (!$match) {
    // Observability: a #color-shaped hash that matches no swatch means the
    // shared link silently fell back to the default color (slug drift,
    // discontinued color, cross-product paste). In-page anchors excluded.
    if (items.length > 0 && !isInPageAnchor(hashSlug)) {
      track("pdp_color_hash_unmatched", { hash: hashSlug });
    }
    return;
  }
  if ($match.classList.contains(SELECTORS.itemActive)) return;

  activateColorItem($context, $match, { userInitiated: false });
};

export default () => {
  const $colorsSlider = document.querySelector(`.${SELECTORS.slider}`);
  if (!$colorsSlider) return;

  initChangeColorHandler($colorsSlider);
  initSliderControls($colorsSlider);
  colorUpdateObserver($colorsSlider);
  scrollToActive($colorsSlider);
  hideSameDayBadges();
  preselectColorFromHash($colorsSlider);

  // Clean up engagement tracking on page unload
  window.addEventListener("beforeunload", () => {
    productEngagementTracker.resetCompositionTracking();
  });

  // Clean up on single-page app navigation
  window.addEventListener("popstate", () => {
    productEngagementTracker.resetCompositionTracking();
  });
};
